import { formatDate, type LedgerEntry } from '@fdt/shared';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { errorKey } from '../api/client';
import { ExportButton } from '../components/ExportButton';
import { MoneyText } from '../components/MoneyText';
import { currentLocale } from '../i18n';
import { customersApi } from '../resources';
import { accountsApi } from '../resources/documents';

/**
 * The label for one statement row.
 *
 * `source` alone cannot separate a payment from the entry that reverses it —
 * both are stored as `payment` — so the reversal is named by the fact that it
 * points at another entry.
 */
function movementLabel(t: (key: string) => string, entry: LedgerEntry): string {
  if (entry.source === 'payment' && entry.reversesEntryId) {
    return t('accounts.paymentReversal');
  }
  return t(`accounts.sources.${entry.source}`);
}

/** A labelled figure in the summary strip. */
function Metric({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

export function CustomerAccountPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();
  const { customerId = '' } = useParams<{ customerId: string }>();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const customer = useQuery({
    queryKey: ['customers', customerId],
    queryFn: () => customersApi.get(customerId),
    enabled: Boolean(customerId),
  });

  const statement = useQuery({
    queryKey: ['statement', customerId, from, to],
    queryFn: () => accountsApi.statement(customerId, { from: from || undefined, to: to || undefined }),
    enabled: Boolean(customerId),
  });

  if (statement.isPending) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (statement.isError || !statement.data) {
    return <Alert severity="error">{t(errorKey(statement.error))}</Alert>;
  }

  const { account, openingBalance, entries, closingBalance } = statement.data;

  return (
    <Stack spacing={2} sx={{ maxWidth: 1100 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton onClick={() => navigate('/customers')} aria-label={t('common.back')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h1">
          {account.customerCode} — {account.customerName}
        </Typography>
      </Stack>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Metric label={t('accounts.balance')}>
              {/* The headline figure: positive means the customer owes money. */}
              <MoneyText value={account.balance} signed variant="h2" />
            </Metric>
            <Metric label={t('accounts.salesTotal')}>
              <MoneyText value={account.salesTotal} />
            </Metric>
            <Metric label={t('accounts.returnsTotal')}>
              <MoneyText value={account.returnsTotal} />
            </Metric>
            <Metric label={t('accounts.paymentsTotal')}>
              <MoneyText value={account.paymentsTotal} />
            </Metric>
            <Metric label={t('accounts.documentCount')}>
              <Typography>{account.documentCount}</Typography>
            </Metric>
            <Metric label={t('accounts.lastDocument')}>
              <Typography>
                {account.lastDocumentOn
                  ? formatDate(`${account.lastDocumentOn}T00:00:00Z`, locale)
                  : '—'}
              </Typography>
            </Metric>
            <Metric label={t('accounts.lastPayment')}>
              <Typography>
                {account.lastPaymentOn
                  ? formatDate(`${account.lastPaymentOn}T00:00:00Z`, locale)
                  : '—'}
              </Typography>
            </Metric>
          </Stack>

          {customer.data && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {[
                  customer.data.address,
                  customer.data.comune,
                  customer.data.provincia,
                  customer.data.vatNumber ? `P.IVA ${customer.data.vatNumber}` : null,
                  customer.data.phone,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Typography variant="h2">{t('accounts.statement')}</Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                label={t('routes.from')}
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                size="small"
                sx={{ width: 165 }}
              />
              <TextField
                label={t('routes.to')}
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                size="small"
                sx={{ width: 165 }}
              />
              <ExportButton
                file="statement.xlsx"
                params={{ customerId, from: from || undefined, to: to || undefined }}
              />
            </Stack>
          </Stack>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t('fields.date')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('accounts.movement')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('documents.number')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t('accounts.debit')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t('accounts.credit')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t('accounts.balance')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* The opening row is what makes a ranged statement add up. */}
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {t('accounts.openingBalance')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <MoneyText value={openingBalance} variant="body2" />
                    </TableCell>
                  </TableRow>

                  {entries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3, textAlign: 'center' }}
                        >
                          {t('accounts.noEntries')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {entries.map((entry: LedgerEntry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(`${entry.occurredOn}T00:00:00Z`, locale)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            variant="outlined"
                            color={entry.reversesEntryId ? 'error' : 'default'}
                            label={movementLabel(t, entry)}
                          />
                          {entry.description && (
                            <Typography variant="caption" color="text.secondary">
                              {entry.description}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {entry.documentNumber ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              cursor: entry.documentId ? 'pointer' : undefined,
                              textDecoration: entry.documentId ? 'underline' : undefined,
                            }}
                            onClick={() =>
                              entry.documentId && navigate(`/documents/${entry.documentId}`)
                            }
                          >
                            {entry.documentNumber}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {entry.direction === 'debit' ? (
                          <MoneyText value={entry.amount} variant="body2" />
                        ) : (
                          ''
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {entry.direction === 'credit' ? (
                          <MoneyText value={entry.amount} variant="body2" />
                        ) : (
                          ''
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <MoneyText value={entry.balance} variant="body2" sx={{ fontWeight: 500 }} />
                      </TableCell>
                    </TableRow>
                  ))}

                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('accounts.closingBalance')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <MoneyText value={closingBalance} signed sx={{ fontWeight: 700 }} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </CardContent>
      </Card>
    </Stack>
  );
}
